import { useEffect, useState } from "react";
import { Card } from "../../ui/Card";
import { useOutletId } from "../../lib/outlet";
import { PageHeader } from "./shared";

type Outlet = { outletId: string; outletName: string };
type Session = { ok: boolean; outlets?: Outlet[] };

type Supplier = Record<string, unknown> & {
  supplierId?: string;
  supplierName?: string;
  companyName?: string;
  status?: string;
  paymentTerm?: string;
};

export function Suppliers() {
  const [sess, setSess] = useState<Session | null>(null);
  const selected = useOutletId(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [perOutlet, setPerOutlet] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void fetch("/api/auth/session").then((r) => r.json()).then(setSess).catch(() => {});
  }, []);

  useEffect(() => {
    const outlets = sess?.outlets ?? [];
    if (outlets.length === 0) return;
    setBusy(true);
    setErr(null);

    // Per-outlet fetch + dedupe by supplierId. The Zeemart endpoint is
    // per-outlet (no comma-list support here), so we fan out + merge.
    Promise.all(
      outlets.map((o) =>
        fetch(
          `/api/zeemart/inventory/services/inventory/deliveryPreference/linkedSuppliers?outletId=${encodeURIComponent(o.outletId)}`,
          { headers: { outletId: o.outletId } },
        )
          .then((r) => r.json())
          .then((d) => {
            const rows = (d?.data ?? d) as Supplier[];
            return Array.isArray(rows) ? rows.map((r) => ({ ...r, _outlet: o.outletId })) : [];
          })
          .catch(() => []),
      ),
    )
      .then((perOutletRows) => {
        const counts: Record<string, number> = {};
        outlets.forEach((o, i) => { counts[o.outletId] = perOutletRows[i].length; });
        setPerOutlet(counts);

        const seen = new Map<string, Supplier>();
        for (const row of perOutletRows.flat()) {
          const sid = String((row as Record<string, unknown>).supplierId ?? (row as Record<string, unknown>).id ?? Math.random());
          if (!seen.has(sid)) seen.set(sid, row);
        }
        const list = Array.from(seen.values());
        // If a single outlet is selected, filter to that one
        const filtered = selected && selected !== "all-outlets"
          ? list.filter((s) => (s as Record<string, unknown>)._outlet === selected)
          : list;
        setSuppliers(filtered);
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setBusy(false));
  }, [sess, selected]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Suppliers"
        subtitle="Linked suppliers across your outlets, fetched via /services/inventory/deliveryPreference/linkedSuppliers."
      />

      {busy && <Card>Loading suppliers...</Card>}
      {err && <Card tone="bad">Could not load suppliers: {err}</Card>}

      {!busy && !err && (
        <Card>
          {suppliers.length === 0 ? (
            <p className="text-ink-mute text-sm">
              No linked suppliers found across the picked outlet(s). Link a supplier on Zeemart and Aiko will pick them up here.
            </p>
          ) : (
            <>
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-xs text-ink-mute uppercase tracking-wider">
                  {suppliers.length} suppliers
                </div>
                <div className="text-xs text-ink-mute">
                  {Object.entries(perOutlet).filter(([, n]) => n > 0).length} outlets active
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-mute">
                    <th className="text-left py-2">Supplier</th>
                    <th className="text-left py-2">Outlet</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-left py-2">Payment term</th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((s, i) => (
                    <tr key={i} className="border-b border-line/60 last:border-0 hover:bg-canvas">
                      <td className="py-2 text-ink">
                        {String(s.supplierName ?? s.companyName ?? s.name ?? s.supplierId ?? "—")}
                      </td>
                      <td className="py-2 text-ink-2">{String((s as Record<string, unknown>)._outlet ?? "—")}</td>
                      <td className="py-2 text-ink-2">{String(s.status ?? "—")}</td>
                      <td className="py-2 text-ink-2">{String(s.paymentTerm ?? "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
